/**
 * Lip Sync Engine
 * Handles real-time lip sync processing and mouth parameter updates
 */

import { SDKEventEmitter } from '../core/EventEmitter';
import { ParameterMapper } from '../live2d/ParameterMapper';
import { createLogger } from '../utils/logger';

import type { LipSyncConfig } from '../types/config';
import type { Live2DModel } from '../types/live2d';


export class LipSyncEngine extends SDKEventEmitter {
  private logger: ReturnType<typeof createLogger>;
  private config: LipSyncConfig;
  private model: Live2DModel | null = null;
  private parameterMapper: ParameterMapper;
  private _isActive = false;
  private mouthParameterIndex = -1;
  private mouthParameterName = '';
  private breathingAnimationFrame: number | null = null;
  private lastVolume = 0;
  private volumeHistory: number[] = [];
  private readonly HISTORY_SIZE = 5;

  constructor(config: LipSyncConfig) {
    super();
    this.logger = createLogger('LipSyncEngine');
    this.config = config;
    this.parameterMapper = new ParameterMapper();
  }

  async initialize(model: Live2DModel): Promise<void> {
    try {
      this.logger.info('Initializing LipSyncEngine...');
      
      if (!model?.internalModel?.coreModel) {
        throw new Error('Invalid Live2D model provided');
      }

      this.model = model;
      
      if (this.config.enabled) {
        await this.setupLipSync();
        this.startBreathingAnimation();
      }

      // Setup global lip sync function for compatibility
      this.setupGlobalFunction();

      this.logger.info('LipSyncEngine initialized successfully');
      
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('LipSyncEngine initialization failed:', err);
      this.emit('error', err);
      throw err;
    }
  }

  /**
   * Process volume data from audio analysis
   */
  processVolume(volume: number): void {
    if (!this.config.enabled || !this.model || this.mouthParameterIndex === -1) {
      return;
    }

    try {
      // Apply smoothing
      const smoothedVolume = this.applySmoothingAndAmplification(volume);
      
      // Update mouth parameter
      this.model.internalModel.coreModel.setParameterValueByIndex(
        this.mouthParameterIndex, 
        smoothedVolume
      );

      this.lastVolume = smoothedVolume;
      this.emit('volumeData', smoothedVolume);

      // Start lip sync if not active
      if (!this._isActive && smoothedVolume > 0.01) {
        this.startLipSync();
      }

      this.logger.debug(`Lip sync volume: ${smoothedVolume.toFixed(3)} (raw: ${volume.toFixed(3)})`);

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('Error processing lip sync volume:', err);
      this.emit('error', err);
    }
  }

  /**
   * Reset mouth to neutral position
   */
  reset(): void {
    if (this.model && this.mouthParameterIndex !== -1) {
      try {
        this.model.internalModel.coreModel.setParameterValueByIndex(this.mouthParameterIndex, 0);
        this.lastVolume = 0;
        this.volumeHistory = [];
        this.logger.debug('Lip sync reset to neutral');
      } catch (error) {
        this.logger.error('Error resetting lip sync:', error);
      }
    }

    this.stopLipSync();
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: LipSyncConfig): void {
    this.logger.info('Updating LipSyncEngine configuration');
    
    const wasEnabled = this.config.enabled;
    this.config = { ...this.config, ...newConfig };

    if (!wasEnabled && this.config.enabled && this.model) {
      // Enable lip sync
      this.setupLipSync();
      this.startBreathingAnimation();
    } else if (wasEnabled && !this.config.enabled) {
      // Disable lip sync
      this.reset();
      this.stopBreathingAnimation();
    }
  }

  /**
   * Check if lip sync is currently active
   */
  isActive(): boolean {
    return this._isActive;
  }

  /**
   * Get current configuration
   */
  getConfig(): LipSyncConfig {
    return { ...this.config };
  }

  /**
   * Destroy and clean up resources
   */
  destroy(): void {
    this.logger.info('Destroying LipSyncEngine...');
    
    try {
      this.reset();
      this.stopBreathingAnimation();
      this.removeAllListeners();
      this.model = null;
      
      // Clean up global function
      if (window.applyLipSync) {
        delete window.applyLipSync;
      }
      
      this.logger.info('LipSyncEngine destroyed');
    } catch (error) {
      this.logger.error('Error during LipSyncEngine destruction:', error);
    }
  }

  // Private methods

  private async setupLipSync(): Promise<void> {
    if (!this.model) return;

    this.logger.info('Setting up lip sync with parameters:', this.config.parameterNames);

    // Find mouth parameter
    const paramNames = this.config.parameterNames.length > 0 ? this.config.parameterNames : this.parameterMapper.getStandardMappings().mouth;
    this.mouthParameterIndex = this.parameterMapper.findParameter(this.model, paramNames);

    if (this.mouthParameterIndex === -1) {
      // Log all available parameters for debugging
      this.logger.warn('No mouth parameter found! Available parameters:');
      this.parameterMapper.logAllParameters(this.model);
      throw new Error('No compatible mouth parameter found in model');
    }

    // Get parameter name safely
    try {
      this.mouthParameterName = this.model.internalModel.coreModel.getParameterName(this.mouthParameterIndex);
      this.logger.info(`Found mouth parameter: ${this.mouthParameterName} at index ${this.mouthParameterIndex}`);
    } catch (error) {
      // Fallback if getParameterName fails
      this.mouthParameterName = `parameter_${this.mouthParameterIndex}`;
      this.logger.info(`Found mouth parameter at index ${this.mouthParameterIndex} (name lookup failed)`);
    }
  }

  private applySmoothingAndAmplification(volume: number): number {
    // Add to volume history for smoothing
    this.volumeHistory.push(volume);
    if (this.volumeHistory.length > this.HISTORY_SIZE) {
      this.volumeHistory.shift();
    }

    // Apply smoothing using history
    const smoothedVolume = this.config.smoothing > 0 
      ? this.lastVolume + (volume - this.lastVolume) * (1 - this.config.smoothing)
      : volume;

    // Apply amplification
    const amplifiedVolume = Math.min(smoothedVolume * this.config.amplification, 1.0);

    return Math.max(amplifiedVolume, 0);
  }

  private startLipSync(): void {
    if (this._isActive) return;

    this._isActive = true;
    this.stopBreathingAnimation();
    this.emit('started');
    this.logger.debug('Lip sync started');
  }

  private stopLipSync(): void {
    if (!this._isActive) return;

    this._isActive = false;
    this.startBreathingAnimation();
    this.emit('ended');
    this.logger.debug('Lip sync stopped');
  }

  private startBreathingAnimation(): void {
    if (this.breathingAnimationFrame || !this.model || !this.config.enabled) return;

    let time = 0;
    const animate = () => {
      if (!this.model || this._isActive || this.mouthParameterIndex === -1) return;

      time += 0.02;
      const breathValue = (Math.sin(time) + 1) * 0.08; // Gentle breathing 0-0.16

      try {
        this.model.internalModel.coreModel.setParameterValueByIndex(this.mouthParameterIndex, breathValue);
      } catch (error) {
        this.logger.error('Error in breathing animation:', error);
        this.stopBreathingAnimation();
        return;
      }

      this.breathingAnimationFrame = requestAnimationFrame(animate);
    };

    animate();
    this.logger.debug('Breathing animation started');
  }

  private stopBreathingAnimation(): void {
    if (this.breathingAnimationFrame) {
      cancelAnimationFrame(this.breathingAnimationFrame);
      this.breathingAnimationFrame = null;
      this.logger.debug('Breathing animation stopped');
    }
  }

  private setupGlobalFunction(): void {
    // Setup global applyLipSync function for compatibility with existing code
    window.applyLipSync = (value: number) => {
      this.processVolume(value);
    };

    this.logger.debug('Global applyLipSync function set up');
  }
}